import { defineStore } from "pinia";
import { getListofEmployee, getListofEmployeesV2 } from "@/api";
import {
  getEmployees as getLocalStorageEmployees,
  updateEmployees,
} from "@components/helper/localStorageHelper";
import { isValidEmployeeId } from "@components/utils/authEmployeeContext";
import { ref, computed } from "vue";

export const useEmployeeStore = defineStore("employee", () => {
  const employees = ref([]);
  const tempEmployees = ref([]);
  const currentEmployee = ref(null);
  const activeEmployeesCount = ref(0);
  const totalEmployeesCount = ref(0);
  const isEmployeesFullyLoaded = ref(false);
  /** Total responsibility assignments for the signed-in employee (Your Stuff → As Employee tab badge). */
  const responsibilitiesAssignmentsTotalCount = ref(null);

  /**
   * Filter-aware header dashboard counts (ER-11183 / MDP-556).
   * Populated from the v2 employee list response when the backend `idea-MDP-558`
   * flag is enabled. Every field is optional — the UI falls back to 0.
   */
  const headerCounts = ref({
    activeCount: 0,
    noAccessCount: 0,
    draftCount: 0,
    activeNoPrimaryPositionCount: 0,
    activeNoPrepareForCount: 0,
  });

  // Pagination state
  const pagination = ref({
    currentPage: 1,
    itemsPerPage: 10,
    totalCount: null,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
    nextUrl: null,
    previousUrl: null,
  });

  const getEmployees = computed(() => employees.value);
  const getActiveEmployeesCount = computed(() => activeEmployeesCount.value);
  const getTotalEmployeesCount = computed(() => totalEmployeesCount.value);
  const getPagination = computed(() => pagination.value);

  function setCurrentEmployee(data) {
    currentEmployee.value = data;
  }

  function resetCurrentEmployee() {
    currentEmployee.value = null;
  }

  function setEmployees(data) {
    const list = Array.isArray(data) ? data : [];
    employees.value = list;
    // Since we're filtering by status=active on server side, all returned employees are active
    activeEmployeesCount.value = list.length;
    updateEmployees(list);
  }

  function setActiveEmployeesCount(data) {
    activeEmployeesCount.value = data;
  }

  function setTotalPositionsCount(count) {
    totalEmployeesCount.value = count;
  }

  function setResponsibilitiesAssignmentsTotalCount(count) {
    responsibilitiesAssignmentsTotalCount.value =
      count == null || count === "" ? null : Number(count);
  }

  function setPagination(paginationData) {
    const merged = {
      ...pagination.value,
      ...paginationData,
    };
    const total = merged.totalCount;
    const perPage = merged.itemsPerPage || 1;
    merged.totalPages =
      total != null && Number.isFinite(total)
        ? Math.ceil(total / perPage)
        : pagination.value.totalPages ?? 0;
    pagination.value = merged;
  }

  /**
   * Store the filter-aware header dashboard counts returned by the v2 employee
   * list (ER-11183). Only numeric values are applied; missing fields fall back
   * to 0 so the dashboard always renders a count, including zero.
   */
  function setHeaderCounts(data) {
    const source = data && typeof data === "object" ? data : {};
    const toCount = (value) => (typeof value === "number" && Number.isFinite(value) ? value : 0);
    headerCounts.value = {
      activeCount: toCount(source.active_count),
      noAccessCount: toCount(source.no_access_count),
      draftCount: toCount(source.draft_count),
      activeNoPrimaryPositionCount: toCount(source.active_no_primary_position_count),
      activeNoPrepareForCount: toCount(source.active_no_prepare_for_count),
    };
  }

  let isLoadingEmployees = false;
  /** Reactive mirror of an in-flight loadEmployees call (lets list UIs avoid flashing an empty state). */
  const isLoadingList = ref(false);
  let lastLoadParams = null;
  let pendingForcedReload = null;
  let latestLoadId = 0;

  function updateEmployeeInList(employeeId, patch) {
    const id = String(employeeId);
    const next = employees.value.map((emp) => (String(emp.id) === id ? { ...emp, ...patch } : emp));
    setEmployees(next);
  }

  function removeEmployeeFromList(employeeId) {
    const id = String(employeeId);
    const next = employees.value.filter((emp) => String(emp.id) !== id);
    setEmployees(next);
    if (totalEmployeesCount.value > 0) {
      totalEmployeesCount.value -= 1;
    }
    if (pagination.value.totalCount > 0) {
      setPagination({ totalCount: pagination.value.totalCount - 1 });
    }
  }

  async function loadEmployees(queryParams = {}, options = {}) {
    const { force = false } = options;

    // Set default pagination if not provided, but prioritize queryParams
    const defaultParams = {
      limit: pagination.value.itemsPerPage,
      offset: (pagination.value.currentPage - 1) * pagination.value.itemsPerPage,
    };

    // Merge queryParams, giving priority to queryParams values
    const finalParams = {
      ...defaultParams,
      ...queryParams,
    };

    // Prevent duplicate concurrent calls with same parameters
    const paramsKey = JSON.stringify(finalParams);
    if (isLoadingEmployees) {
      if (force) {
        pendingForcedReload = { queryParams, options };
        return;
      }
      if (lastLoadParams === paramsKey) {
        return;
      }
    }

    isLoadingEmployees = true;
    isLoadingList.value = true;
    lastLoadParams = paramsKey;
    const loadId = ++latestLoadId;

    try {
      if (!force) {
        setEmployees(getLocalStorageEmployees());
      }
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      let response = await getListofEmployeesV2({
        account_id: accountId,
        queryParamsObj: finalParams,
      });

      // A newer load started while this one was in flight: its result wins.
      if (loadId !== latestLoadId) {
        return;
      }

      if (response.data?.count) {
        totalEmployeesCount.value = response.data.count;
      }

      setHeaderCounts(response.data);

      if (response.data?.results && response.data.results.length > 0) {
        updateEmployees(response.data.results); // localstorage
        setEmployees(response.data.results); // store

        // Update pagination info
        setPagination({
          totalCount: response.data.count || 0,
          hasNext: !!response.data.next,
          hasPrevious: !!response.data.previous,
          nextUrl: response.data.next,
          previousUrl: response.data.previous,
          itemsPerPage: finalParams.limit,
        });

        // Update total count for active employees
        totalEmployeesCount.value = response.data.count || 0;
      } else {
        // No results (search/filter returned empty or API omitted results) – clear list so "No record found" shows
        setEmployees([]);
        setPagination({
          totalCount: response.data?.count ?? 0,
          hasNext: !!response.data?.next,
          hasPrevious: !!response.data?.previous,
          nextUrl: response.data?.next ?? null,
          previousUrl: response.data?.previous ?? null,
          itemsPerPage: finalParams.limit,
        });
        totalEmployeesCount.value = response.data?.count ?? 0;
      }
    } catch (e) {
      console.error("error fetching employees: ", e);
    } finally {
      if (loadId === latestLoadId) {
        isLoadingEmployees = false;
        isLoadingList.value = false;
        lastLoadParams = null;
        if (pendingForcedReload) {
          const pending = pendingForcedReload;
          pendingForcedReload = null;
          await loadEmployees(pending.queryParams, pending.options);
        }
      }
    }
  }

  async function loadEmployeesPage(page, itemsPerPage = pagination.value.itemsPerPage) {
    setPagination({ currentPage: page, itemsPerPage });
    await loadEmployees();
  }

  async function loadNextPage() {
    if (pagination.value.hasNext && pagination.value.currentPage < pagination.value.totalPages) {
      await loadEmployeesPage(pagination.value.currentPage + 1);
    }
  }

  async function loadPreviousPage() {
    if (pagination.value.hasPrevious && pagination.value.currentPage > 1) {
      await loadEmployeesPage(pagination.value.currentPage - 1);
    }
  }

  async function loadEmployeesBatch(page = 1, size = 50) {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const response = await getListofEmployee(accountId, page, size);

      if (response.data?.results) {
        const current = page === 1 ? [] : employees.value;
        const newList = [...current, ...response.data.results];
        updateEmployees(newList);
        setEmployees(newList);
      }

      if (response.data?.count) {
        totalEmployeesCount.value = response.data.count;
      }

      return response.data;
    } catch (e) {
      console.error("error fetching employees: ", e);
    }
  }

  async function fetchRemainingEmployeesInBatches() {
    const size = 50;
    const concurrency = 5;

    const total = totalEmployeesCount.value || 500;
    const alreadyLoaded = employees.value.length;
    const pagesAlreadyFetched = Math.ceil(alreadyLoaded / size);
    const totalPagesNeeded = Math.ceil(total / size);
    const pagesRemaining = totalPagesNeeded - pagesAlreadyFetched;

    if (pagesRemaining <= 0) {
      isEmployeesFullyLoaded.value = true;
      return;
    }

    tempEmployees.value = [];
    let currentPage = pagesAlreadyFetched + 1;

    while (currentPage <= totalPagesNeeded) {
      const requests = [];

      for (let i = 0; i < concurrency && currentPage <= totalPagesNeeded; i++) {
        requests.push(loadEmployeesBatchToTemp(currentPage, size));
        currentPage++;
      }

      await Promise.all(requests);
    }

    const allEmployees = [...employees.value, ...tempEmployees.value];
    setEmployees(allEmployees);
    updateEmployees(allEmployees);
    tempEmployees.value = [];

    isEmployeesFullyLoaded.value = true;
  }

  async function loadEmployeesBatchToTemp(page, size) {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const response = await getListofEmployee(accountId, page, size);

      if (response.data?.results) {
        tempEmployees.value = [...tempEmployees.value, ...response.data.results];
      }

      if (response.data?.count) {
        totalEmployeesCount.value = response.data.count;
      }

      return response.data;
    } catch (e) {
      console.error("error fetching employees: ", e);
    }
  }

  async function loadEmployeesFromServer() {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const { data } = await getListofEmployee(accountId, 1, 500);
      return data?.results || [];
    } catch (e) {
      console.error("error fetching employees from server: ", e);
      return [];
    }
  }

  // Track ongoing requests to prevent duplicate API calls
  const pendingRequests = new Map();

  // Centralized method to fetch current employee data
  async function fetchCurrentEmployee(empId, forceRefresh = false) {
    try {
      if (!isValidEmployeeId(empId)) {
        return null;
      }
      // If we already have the current employee data and not forcing refresh, return it
      if (!forceRefresh && currentEmployee.value && currentEmployee.value.id == empId) {
        return currentEmployee.value;
      }

      // Check if there's already a pending request for this employee
      if (pendingRequests.has(empId)) {
        // Wait for the existing request to complete
        return await pendingRequests.get(empId);
      }

      // Create a new request promise
      const requestPromise = (async () => {
        try {
          const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

          // Import getEmployeeById dynamically to avoid circular dependency
          const { getEmployeeById } = await import("@/api");
          const response = await getEmployeeById(accountId, empId);

          if (response?.data) {
            setCurrentEmployee(response.data);
            // Also update Vuex store for backwards compatibility
            // This will be handled by the component that calls this method
            return response.data;
          }

          return null;
        } finally {
          // Remove the promise from pending requests once completed
          pendingRequests.delete(empId);
        }
      })();

      // Store the promise so concurrent calls can await it
      pendingRequests.set(empId, requestPromise);

      return await requestPromise;
    } catch (error) {
      // Remove from pending requests on error
      pendingRequests.delete(empId);
      console.error("Error fetching current employee:", error);
      // Check if it's a 404 error and throw it to allow proper error handling
      if (error.response && error.response.status === 404) {
        throw error;
      }
      return null;
    }
  }

  // Fetch employee chatter via API — used by employee header components.
  async function fetchEmployeeChatter(accountId, employeeId, page = 1) {
    if (!accountId || !employeeId) {
      return null;
    }

    const { getChatterList } = await import("@/api");
    return getChatterList(accountId, employeeId, "employee", page);
  }

  // Centralized method to recalculate employee readiness
  async function recalculateCurrentEmployeeReadiness(empId, forceRecalc = true) {
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      // Import recalculateEmployeeReadiness dynamically to avoid circular dependency
      const { recalculateEmployeeReadiness } = await import("@/api");
      const response = await recalculateEmployeeReadiness(accountId, empId, forceRecalc);

      if (response?.data) {
        // Update current employee with new readiness data
        if (currentEmployee.value) {
          currentEmployee.value = {
            ...currentEmployee.value,
            current_readiness: response.data.current_readiness,
            needs_recalc: response.data.needs_recalc,
            readiness_status: response.data.readiness_status,
          };
        }
        return response.data;
      }

      return null;
    } catch (error) {
      console.error("Error recalculating employee readiness:", error);
      return null;
    }
  }

  return {
    employees,
    currentEmployee,
    activeEmployeesCount,
    totalEmployeesCount,
    isEmployeesFullyLoaded,
    responsibilitiesAssignmentsTotalCount,
    headerCounts,
    pagination,
    isLoadingList,
    getEmployees,
    getActiveEmployeesCount,
    getTotalEmployeesCount,
    getPagination,
    setCurrentEmployee,
    resetCurrentEmployee,
    setEmployees,
    updateEmployeeInList,
    removeEmployeeFromList,
    setActiveEmployeesCount,
    setTotalPositionsCount,
    setResponsibilitiesAssignmentsTotalCount,
    setHeaderCounts,
    setPagination,
    loadEmployees,
    loadEmployeesPage,
    loadNextPage,
    loadPreviousPage,
    loadEmployeesFromServer,
    loadEmployeesBatch,
    fetchRemainingEmployeesInBatches,
    fetchCurrentEmployee,
    fetchEmployeeChatter,
    recalculateCurrentEmployeeReadiness,
  };
});
